import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import {
  useESGDataEntryStore,
  useESGDisclosureSelectionStore,
  useESGReportBuilderStore,
} from "@/features/esg/hooks/useESGReportStore";
import { frameworkInfos } from "@/lib/constants/frameworks";
import { useTran } from "@/hooks";
import {
  CATEGORY_ORDER,
  buildDisclosureMap,
  buildDisclosuresByPillar,
  buildPillarCounts,
  getDisclosureValue,
} from "./print-utils";
import { resolveCodeForFramework } from "@/features/esg/utils/exportHelpers";
import ESGReportCoverPage from "./report/ESGReportCoverPage";
import ESGReportTableOfContents from "./report/ESGReportTableOfContents";
import ESGFrameworkOverview from "./report/ESGFrameworkOverview";
import ESGSustainabilityStrategy from "./report/ESGSustainabilityStrategy";
import ESGPillarSummary from "./report/ESGPillarSummary";
import ESGPerformanceTable from "./report/ESGPerformanceTable";
import ESGReportAppendix from "./report/ESGReportAppendix";

const ESGPrintableReport = observer(
  ({ exportFramework = "All", metricMappings = [] }) => {
    const { t } = useTran();
    const {
      reportName,
      framework,
      orgName,
      notes,
      includeCover,
      includeToc,
      includeAppendix,
    } = useESGReportBuilderStore();
    const { availableDisclosures, customDisclosures, selectedCodesByCategory } =
      useESGDisclosureSelectionStore();
    const { entriesByCode, isDisclosureCompleted, progressPercentage } =
      useESGDataEntryStore();

    // When exporting specific framework, use that for display; otherwise use base framework
    const displayFramework =
      exportFramework !== "All" ? exportFramework : framework;
    const frameworkInfo =
      frameworkInfos.find((info) => info.key === displayFramework) ||
      frameworkInfos[0];

    const disclosureMap = useMemo(
      () => buildDisclosureMap([...availableDisclosures, ...customDisclosures]),
      [availableDisclosures, customDisclosures],
    );

    const disclosuresByPillar = useMemo(
      () => buildDisclosuresByPillar(selectedCodesByCategory, disclosureMap),
      [selectedCodesByCategory, disclosureMap],
    );

    const pillarCounts = useMemo(
      () => buildPillarCounts(selectedCodesByCategory, isDisclosureCompleted),
      [selectedCodesByCategory, isDisclosureCompleted],
    );

    const totalDisclosures = Object.values(pillarCounts).reduce(
      (sum, pillar) => sum + pillar.total,
      0,
    );

    const defaultStrategy = {
      overview: t(
        "This report summarizes our sustainability priorities and performance across environmental, social, and governance pillars.",
      ),
      environmental: [
        t("Reduce greenhouse gas emissions year over year."),
        t("Improve energy efficiency and renewable energy adoption."),
        t("Strengthen waste management and resource stewardship."),
      ],
      social: [
        t("Invest in employee wellbeing and safety."),
        t("Promote diversity, equity, and inclusion."),
        t("Support community engagement and development."),
      ],
      governance: [
        t("Enhance board oversight and accountability."),
        t("Strengthen risk management and compliance."),
        t("Promote ethical conduct across the organization."),
      ],
    };

    const strategy = {
      ...defaultStrategy,
      overview: notes || defaultStrategy.overview,
    };

    const resolvedDisclosuresByPillar = useMemo(() => {
      const result = {};
      CATEGORY_ORDER.forEach((pillar) => {
        result[pillar] = (disclosuresByPillar[pillar] || [])
          .map((d) => {
            const resolved = resolveCodeForFramework(
              d.code,
              d,
              exportFramework,
              metricMappings,
            );
            if (!resolved) return null;
            return {
              code: d.code,
              title: resolved.title,
              displayCode: resolved.code,
            };
          })
          .filter(Boolean);
      });
      return result;
    }, [disclosuresByPillar, exportFramework, metricMappings]);

    const resolveDisclosureValue = (code) => {
      const entries = entriesByCode.get(code) || [];
      return getDisclosureValue(entries, {
        entries: t("entries"),
        items: t("items"),
      });
    };

    return (
      <div className="esg-report-body bg-white text-black">
        {includeCover ? (
          <ESGReportCoverPage
            reportName={reportName}
            framework={displayFramework}
            notes={notes}
            orgName={orgName}
          />
        ) : null}

        {includeToc ? <ESGReportTableOfContents /> : null}

        <ESGFrameworkOverview
          framework={displayFramework}
          reportName={reportName}
          frameworkInfo={frameworkInfo}
          totalDisclosures={totalDisclosures}
          completionPercentage={progressPercentage}
          orgName={orgName}
        />

        <ESGSustainabilityStrategy strategy={strategy} />

        <ESGPillarSummary pillarCounts={pillarCounts} />

        <ESGPerformanceTable
          pillar="Environment"
          disclosures={resolvedDisclosuresByPillar.Environment}
          color="#3b82f6"
          getDisclosureValue={resolveDisclosureValue}
          isDisclosureCompleted={isDisclosureCompleted}
          className="esg-page-break"
        />
        <ESGPerformanceTable
          pillar="Social"
          disclosures={resolvedDisclosuresByPillar.Social}
          color="#8b5cf6"
          getDisclosureValue={resolveDisclosureValue}
          isDisclosureCompleted={isDisclosureCompleted}
          className="esg-page-break"
        />
        <ESGPerformanceTable
          pillar="Governance"
          disclosures={resolvedDisclosuresByPillar.Governance}
          color="#f59e0b"
          getDisclosureValue={resolveDisclosureValue}
          isDisclosureCompleted={isDisclosureCompleted}
          className="esg-page-break"
        />

        {includeAppendix ? (
          <ESGReportAppendix framework={displayFramework} />
        ) : null}
      </div>
    );
  },
);

export default ESGPrintableReport;
